import React, { useState } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import Coupon from 'billing/models/Coupon'
import Button from 'components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import { MembershipsTabViewModel } from 'user-management/views/MembershipsTab/MembershipsTabViewModel'
import {
  DateZero,
  formatToLongDate,
  formatToLongDateWithComma,
} from 'utils/date-util'
import { pluralize } from 'utils/pluralization-util'
import { parseCapacity } from '../UserDetailsInformation'
import styles from './MembershipsSidebar.sass'

interface MembershipsSidebarProps {
  vm: MembershipsTabViewModel
}

export default observer(({ vm }: MembershipsSidebarProps) => {
  const membership = vm.selectedMembership || {
    member: Member.none,
    workspace: Workspace.none,
  }
  const workspaceAccess = membership.workspace.id
    ? vm.workspaceAccess[membership.workspace.id]
    : null

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <IconButton onClick={() => vm.closeSidebar()}>
            <Icon name="back" size={24} />
          </IconButton>
        </div>
        <Dropdown
          autoDismiss
          position="bottom-end"
          button={
            <IconButton
              dataTest={`memberships-sidebar-actions-${membership.workspace.id}`}
            >
              <Icon name="more" size={24} />
            </IconButton>
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onClick={() =>
              vm.showAddUserToTeamDialogForWorkspace(membership.workspace)
            }
            dataTest="memberships-sidebar-actions-add-to-team"
          >
            Add user to team
          </DropdownItem>
          <DropdownItem
            onClick={() => vm.editUserRole(vm.user, membership.workspace)}
            dataTest={`memberships-sidebar-actions-edit-role-${membership.workspace.id}`}
          >
            Edit role
          </DropdownItem>
          <DropdownItem
            onClick={() => vm.editSkills(vm.user, membership.workspace)}
            dataTest={`memberships-sidebar-actions-edit-skills-${membership.workspace.id}`}
          >
            Edit skills
          </DropdownItem>
          <DropdownItem
            onClick={() => vm.manageCapacity(vm.user, membership.workspace)}
            dataTest={`memberships-sidebar-actions-manage-capacity-${membership.workspace.id}`}
          >
            Manage capacity
          </DropdownItem>
          <DropdownItem
            onClick={() =>
              vm.removeFromWorkspace(vm.user, membership.workspace)
            }
            dataTest={`memberships-sidebar-actions-remove-from-workspace-${membership.workspace.id}`}
          >
            Remove from workspace
          </DropdownItem>
        </Dropdown>
      </div>
      <div className={styles.membershipInfo}>
        <h2 className={styles.membershipInfoTitle}>
          {membership.workspace.name}
        </h2>
        <div className={styles.membershipInfoGrid}>
          <MembershipInfoGridRow
            title="Role"
            value={membership.member.roles.find((r) => r.name)?.name}
          />
          <MembershipInfoGridRow
            title="Joined"
            value={formatToLongDateWithComma(
              membership.member.dateCreated || DateZero(),
            )}
          />
          <MembershipInfoGridRow
            title="Active jobs"
            value={`${vm.jobTotals.activeJobs} / ${parseCapacity(
              membership.member.maxConcurrentProviderJobs,
            )}`}
          />
          <TotalJobsGridRow vm={vm} />
        </div>
      </div>
      <div className={styles.membershipItems}>
        {!vm.isPro && (
          <Accordion
            title="Coupons redeemed"
            items={vm.coupons}
            renderItem={(userCoupon) => (
              <AccordionItem
                key={userCoupon.dateRedeemed.toUTCString()}
                title={userCoupon.coupon.code}
                addendum={`${formatCouponValue(userCoupon.coupon)}`}
                subtitle={`Redeemed ${formatToLongDate(userCoupon.dateRedeemed)}`}
              />
            )}
          />
        )}
        <Accordion
          title="Skills"
          items={membership.member.skills || []}
          renderItem={(skill) => (
            <AccordionItem
              key={skill.id}
              title={skill.name}
              subtitle={skill.description}
              dataTest={`memberships-sidebar-skill-${skill.id}`}
            />
          )}
          buttonText={workspaceAccess?.canEditUserSkills ? 'Edit' : ''}
          buttonAction={() => vm.editSkills(vm.user, membership.workspace)}
          dataTest="memberships-sidebar-manage-skills"
        />
        <Accordion
          title={`${pluralize('Team', vm.teamMembers.length)}`}
          items={vm.teamMembers}
          renderItem={(teamMember) => (
            <AccordionItem
              key={teamMember.team.id}
              title={teamMember.team.name}
              subtitle={teamMember.teamRole.name}
              dataTest={`memberships-sidebar-team-${teamMember.team.id}`}
            />
          )}
        />
      </div>
    </div>
  )
})

interface MembershipInfoGridRowProps {
  title: string
  value?: string | number
}

const MembershipInfoGridRow = observer(
  ({ title, value }: MembershipInfoGridRowProps) => (
    <>
      <div className={styles.membershipInfoGridTitle}>{title}</div>
      <div className={styles.membershipInfoGridValue}>{value}</div>
    </>
  ),
)

interface AccordionProps<T> {
  title: string
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  buttonText?: string
  buttonAction?: () => void
  dataTest?: string
}

const Accordion = observer(function Accordion<T>({
  title,
  items,
  renderItem,
  buttonText,
  buttonAction,
  dataTest,
}: AccordionProps<T>) {
  const [isExtended, setIsExtended] = useState(true)

  const toggleIsExtended = () => {
    setIsExtended(!isExtended)
  }

  return (
    <div className={styles.accordion} data-test={dataTest}>
      <div className={styles.accordionHeader}>
        <IconButton
          className={cx(
            styles.accordionHeaderIcon,
            !isExtended && styles.rotate,
          )}
          onClick={toggleIsExtended}
        >
          <Icon name="arrow" size={24} />
        </IconButton>
        <div className={styles.accordionHeaderText}>
          <span className={styles.accordionHeaderTitle}>
            {title}
            <span className={styles.accordionHeaderCount}>
              {`(${items.length})`}
            </span>
          </span>
        </div>
        {buttonText && buttonAction && (
          <Button
            className={styles.accordionAction}
            type="text"
            onClick={buttonAction}
          >
            {buttonText}
          </Button>
        )}
      </div>
      <div
        className={cx(styles.accordionContent, !isExtended && styles.contract)}
      >
        {items.map((item, index) => renderItem(item, index))}
      </div>
    </div>
  )
})

interface AccordionItemProps {
  title: string
  addendum?: string
  subtitle?: string
  onClick?: () => void
  dataTest?: string
}

const AccordionItem = observer(
  ({ title, addendum, subtitle, onClick, dataTest }: AccordionItemProps) => {
    return (
      <div className={styles.accordionItem} data-test={dataTest}>
        <div className={styles.accordionItemText}>
          <div className={styles.accordionItemTitle}>
            <h5>{title}</h5>
            {addendum && (
              <>
                <span className={styles.accordionItemDivider}>•</span>
                <span className={styles.accordionItemAddendum}>{addendum}</span>
              </>
            )}
          </div>
          {subtitle && (
            <div className={styles.accordionItemSubtitle}>{subtitle}</div>
          )}
        </div>
        {onClick && (
          <IconButton onClick={onClick} data-test={dataTest}>
            <Icon name="arrow" size={24} color="var(--grey600)" />
          </IconButton>
        )}
      </div>
    )
  },
)

function formatCouponValue(coupon: Coupon) {
  switch (coupon.offType.type) {
    case 'AMOUNT_OFF':
      return `$${coupon.offType.amountOff} OFF`
    case 'PERCENT_OFF':
      return `${coupon.offType.percentOff}% OFF`
  }
}

interface TotalJobsGridRowProps {
  vm: MembershipsTabViewModel
}

const TotalJobsGridRow = observer(({ vm }: TotalJobsGridRowProps) => {
  return (
    <>
      <div className={styles.membershipInfoGridTitle}>Total jobs</div>
      <div className={styles.membershipTotalJobs}>
        {vm.jobTotals.totalJobs}
        {!!vm.jobTotals.totalJobs && (
          <Dropdown
            width="miniscule"
            autoDismiss
            button={
              <IconButton>
                <Icon name="dropdown" color="var(--grey600)" size={14} />
              </IconButton>
            }
          >
            <DropdownTitle>Total jobs</DropdownTitle>

            {!!vm.jobTotals.infoGatheringJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Info gathering"
                  value={vm.jobTotals.infoGatheringJobs}
                />
              </DropdownItem>
            )}

            {!!vm.jobTotals.unclaimedJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Unclaimed"
                  value={vm.jobTotals.unclaimedJobs}
                />
              </DropdownItem>
            )}

            {!!vm.jobTotals.claimedJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="In progress"
                  value={vm.jobTotals.claimedJobs}
                />
              </DropdownItem>
            )}

            {!!vm.jobTotals.idleJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Idle"
                  value={vm.jobTotals.idleJobs}
                />
              </DropdownItem>
            )}

            {!!vm.jobTotals.onHoldJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="On hold"
                  value={vm.jobTotals.onHoldJobs}
                />
              </DropdownItem>
            )}

            {!!vm.jobTotals.closedJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Completed"
                  value={vm.jobTotals.closedJobs}
                />
              </DropdownItem>
            )}

            {!!vm.jobTotals.underConstructionJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Under construction"
                  value={vm.jobTotals.underConstructionJobs}
                />
              </DropdownItem>
            )}
          </Dropdown>
        )}
      </div>
    </>
  )
})
